/**
 * DecisionQuickCapture (FLOW-570)
 *
 * The ad-hoc capture path, independent of any intake session: type what was
 * decided, let the model turn it into a structured draft, adjust the fields,
 * save. Persists nothing until Save — drafting is a preview, matching the
 * `POST .../decisions/draft` contract.
 *
 * Mounted once at the Plan page level so it's available no matter what state
 * the intake flow is in; collapsed by default so it doesn't compete with
 * whatever the page is otherwise showing.
 */

import { Component, For, Show, createSignal } from "solid-js";
import { Button, Input, Spinner } from "../ui";
import { createDecision, draftDecision } from "../../api/decisions";
import { describeAiError, surfaceQuotaExhausted } from "../../utils/aiError";
import { useConflictCheck } from "../../hooks";
import { DecisionConflictWarnings } from "../decisions/DecisionConflictWarnings";
import type {
  ConflictResolution,
  CreateDecisionRequest,
  DecisionCategory,
  DecisionConfidence,
  ExtractedDecision,
  ProjectId,
} from "../../api/types";

export interface DecisionQuickCaptureProps {
  projectId: ProjectId;
}

const CATEGORIES: { value: DecisionCategory; label: string }[] = [
  { value: "architecture", label: "Architecture" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "convention", label: "Convention" },
  { value: "dependency", label: "Dependency" },
  { value: "security", label: "Security" },
  { value: "performance", label: "Performance" },
  { value: "process", label: "Process" },
];

const CONFIDENCES: { value: DecisionConfidence; label: string }[] = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export const DecisionQuickCapture: Component<DecisionQuickCaptureProps> = (
  props,
) => {
  const [open, setOpen] = createSignal(false);
  const [text, setText] = createSignal("");
  const [drafting, setDrafting] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const [draft, setDraft] = createSignal<ExtractedDecision | null>(null);
  const [title, setTitle] = createSignal("");
  const [rationale, setRationale] = createSignal("");
  const [category, setCategory] =
    createSignal<DecisionCategory>("architecture");
  const [confidence, setConfidence] =
    createSignal<DecisionConfidence>("medium");
  const [tagsText, setTagsText] = createSignal("");

  // FLOW-577: conflict check on save (D1). `reviewing` is the UI's own
  // "show the warnings panel" flag — kept separate from the hook's `state`
  // so `accepted_edited` can return to the form without discarding the
  // hook's per-subject dedup (see the `accepted_edited` branch of
  // `applyChoices`).
  const conflictCheck = useConflictCheck(() => props.projectId);
  const [reviewing, setReviewing] = createSignal(false);
  const [choices, setChoices] = createSignal<
    Record<string, ConflictResolution | undefined>
  >({});
  // Set once a decision has been persisted for the current warnings, so a
  // failure in the edges/resolve step that follows can never trigger a
  // second `createDecision` call (the decision already exists).
  const [saved, setSaved] = createSignal(false);

  const allWarningsChosen = () =>
    conflictCheck
      .warnings()
      .every((warning) => choices()[warning.id] !== undefined);

  const handleChoose = (warningId: string, outcome: ConflictResolution) => {
    setChoices((prev) => ({ ...prev, [warningId]: outcome }));
  };

  /**
   * Everything that belongs to the draft, including the conflict review it
   * raised. Discard is clickable while the warnings are showing, and leaving
   * that state live would let the *next* draft be saved against these
   * warnings with no check of its own — an unchecked save (D1), edges to the
   * wrong decisions, and acceptances recorded for a decision nobody reviewed.
   */
  const resetDraft = () => {
    setDraft(null);
    setTitle("");
    setRationale("");
    setCategory("architecture");
    setConfidence("medium");
    setTagsText("");
    setError(null);
    setReviewing(false);
    setChoices({});
    setSaved(false);
    conflictCheck.reset();
  };

  const close = () => {
    setOpen(false);
    setText("");
    resetDraft();
  };

  const requestDraft = async () => {
    const trimmed = text().trim();
    if (!trimmed || drafting()) return;
    setDrafting(true);
    setError(null);
    try {
      const response = await draftDecision(props.projectId, trimmed);
      setDraft(response.decision);
      setTitle(response.decision.title);
      setRationale(response.decision.rationale);
      setCategory(response.decision.category);
      setConfidence(response.decision.confidence);
      setTagsText(response.decision.tags.join(", "));
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
    } finally {
      setDrafting(false);
    }
  };

  const buildBody = (): CreateDecisionRequest => {
    const tags = tagsText()
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    return {
      title: title().trim(),
      rationale: rationale().trim(),
      context: draft()?.context ?? undefined,
      category: category(),
      confidence: confidence(),
      source_type: "manual",
      tags,
    };
  };

  /** Applies the collected choices once every warning has one (D2). */
  const applyChoices = async () => {
    setSaving(true);
    setError(null);

    const chosen = choices();
    const anyEdited = conflictCheck
      .warnings()
      .some((warning) => chosen[warning.id] === "accepted_edited");

    if (anyEdited) {
      // D3: accepted_edited saves nothing and returns to the edit form. That
      // is the only consequence that lands here, so `applyOutcomes(null, …)`
      // records only the accepted_edited warnings — a sibling given some
      // other outcome stays pending for the re-check that follows the edit
      // (D4), rather than being counted as accepted with nothing behind it.
      try {
        await conflictCheck.applyOutcomes(null, chosen);
        setChoices({});
        setReviewing(false);
      } catch (err) {
        setError(describeAiError(err));
      } finally {
        setSaving(false);
      }
      return;
    }

    let created;
    try {
      created = await createDecision(props.projectId, buildBody());
    } catch (err) {
      setError(describeAiError(err));
      setSaving(false);
      return;
    }
    setSaved(true);

    // D4: the consequence (edge, if any) lands before the warning is
    // resolved. A failure here must not pretend the save failed, and must
    // never trigger a second createDecision — the decision already exists.
    //
    // Note the divergence from the captured queue, which confirms a decision
    // to `active` before writing its edges: a decision saved here is still
    // `proposed` (CreateDecisionRequest carries no status and quick capture
    // has no confirm step), so an `accepted_superseded` choice retires the
    // existing decision on behalf of a proposed one. Deliberate: the user
    // drafted, edited, saved and then explicitly chose to supersede, and the
    // supersession reverses if the edge is removed.
    try {
      await conflictCheck.applyOutcomes(created.id, chosen);
      setSaving(false);
      close();
    } catch (err) {
      setError(
        `The decision was saved, but recording the resolution failed: ${describeAiError(err)}`,
      );
      setSaving(false);
    }
  };

  const save = async () => {
    if (!title().trim() || !rationale().trim() || saving() || saved()) return;

    if (reviewing()) {
      if (!allWarningsChosen()) return;
      await applyChoices();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const state = await conflictCheck.check({
        title: title().trim(),
        rationale: rationale().trim(),
        context: draft()?.context ?? undefined,
        category: category(),
      });
      if (state === "warnings") {
        setReviewing(true);
        return;
      }
      // "clear" or "unavailable" (detector down/erroring/timed out) save
      // exactly as before — detection enriches, it never gates.
      await createDecision(props.projectId, buildBody());
      close();
    } catch (err) {
      setError(describeAiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="mt-6">
      <Show
        when={open()}
        fallback={
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Capture a decision
          </Button>
        }
      >
        <section
          class="rounded-md border border-line bg-surface p-6"
          aria-labelledby="quick-capture-title"
        >
          <div class="flex items-center justify-between gap-4">
            <h2 id="quick-capture-title" class="text-lg font-semibold text-ink">
              Capture a decision
            </h2>
            <button
              type="button"
              aria-label="Close decision capture"
              onClick={close}
              class="shrink-0 text-ink-3 hover:text-ink-2 focus-ring rounded"
            >
              <CloseIcon />
            </button>
          </div>

          <Show when={error()}>
            {(message) => (
              <div
                role="alert"
                class="mt-3 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
              >
                <p class="text-sm text-signal-red">{message()}</p>
              </div>
            )}
          </Show>

          <Show
            when={draft()}
            fallback={
              <div class="mt-4 flex flex-col gap-3">
                <textarea
                  value={text()}
                  onInput={(e) => setText(e.currentTarget.value)}
                  placeholder="Describe the decision — what was decided and why…"
                  disabled={drafting()}
                  aria-label="Decision description"
                  autofocus
                  rows={3}
                  class="w-full resize-none rounded border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 disabled:bg-sunken disabled:text-ink-3"
                />
                <div class="flex items-center justify-end gap-3">
                  <Show when={drafting()}>
                    <span class="flex items-center gap-2 text-sm text-ink-3">
                      <Spinner size="sm" />
                      Drafting…
                    </span>
                  </Show>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={drafting() || !text().trim()}
                    onClick={() => void requestDraft()}
                  >
                    Draft
                  </Button>
                </div>
              </div>
            }
          >
            <div class="mt-4 flex flex-col gap-4">
              <Input label="Title" value={title()} onInput={setTitle} />
              <Input
                label="Rationale"
                isTextarea
                rows={3}
                value={rationale()}
                onInput={setRationale}
              />

              <div class="grid grid-cols-2 gap-3">
                <label class="block text-sm">
                  <span class="mb-1 block font-medium text-ink-2">
                    Category
                  </span>
                  <select
                    aria-label="Category"
                    class="w-full rounded border border-line px-3 py-2 text-sm text-ink"
                    value={category()}
                    onChange={(e) =>
                      setCategory(e.currentTarget.value as DecisionCategory)
                    }
                  >
                    <For each={CATEGORIES}>
                      {(option) => (
                        <option value={option.value}>{option.label}</option>
                      )}
                    </For>
                  </select>
                </label>

                <label class="block text-sm">
                  <span class="mb-1 block font-medium text-ink-2">
                    Confidence
                  </span>
                  <select
                    aria-label="Confidence"
                    class="w-full rounded border border-line px-3 py-2 text-sm text-ink"
                    value={confidence()}
                    onChange={(e) =>
                      setConfidence(e.currentTarget.value as DecisionConfidence)
                    }
                  >
                    <For each={CONFIDENCES}>
                      {(option) => (
                        <option value={option.value}>{option.label}</option>
                      )}
                    </For>
                  </select>
                </label>
              </div>

              <Input
                label="Tags"
                placeholder="billing, infra"
                value={tagsText()}
                onInput={setTagsText}
              />

              <Show when={reviewing()}>
                <DecisionConflictWarnings
                  warnings={conflictCheck.warnings()}
                  choices={choices()}
                  onChoose={handleChoose}
                  allowEdited={true}
                  busy={saving()}
                />
              </Show>

              <div class="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={resetDraft}>
                  Discard
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  loading={saving()}
                  disabled={
                    !title().trim() ||
                    !rationale().trim() ||
                    saved() ||
                    (reviewing() && !allWarningsChosen())
                  }
                  onClick={() => void save()}
                >
                  Save
                </Button>
              </div>
            </div>
          </Show>
        </section>
      </Show>
    </div>
  );
};

const CloseIcon: Component = () => (
  <svg
    class="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default DecisionQuickCapture;
